import { useRef, useState } from 'react';
import { useSwimlaneSlot } from '@/components/ui/swimlane/context';
import {
  MAX_CARD_SUBITEMS_HEIGHT,
  SUBITEM_GAP,
  SUBITEM_HEIGHT,
} from '@/components/ui/swimlane/layout/constants';
import { bindOpenCardDetailsHandler } from '@/components/ui/swimlane/layout/hooks/useCardEventHandlers';
import type { ICardProps, SubItem } from '@/components/ui/swimlane/types';

import { AddSubItemInput } from './AddSubItemInput';
import { SubItemHeader } from './SubItemHeader';

const listItemButtonStyle: React.CSSProperties = {
  padding: '4px 10px',
  height: SUBITEM_HEIGHT.label,
  backgroundColor: '#E5F0FB',
  borderRadius: '4px',
  border: 'none',
  cursor: 'pointer',
  width: '100%',
  textAlign: 'left',
  verticalAlign: 'middle',
  display: 'flex',
};

const listItemTextStyle: React.CSSProperties = {
  width: '100%',
  maxWidth: '100%',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
  lineHeight: '16px',
  fontSize: '12px',
};

type SubItemsProps = ICardProps & {
  expanded: boolean;
  subItems: SubItem[];
  readOnly: boolean;
  maxSubItemsPerItem: number;
};

export function SubItems({
  itemID,
  rowID,
  columnID,
  expanded,
  subItems,
  readOnly,
  maxSubItemsPerItem,
}: SubItemsProps) {
  const listRef = useRef<HTMLUListElement>(null);
  const [showTextField, setShowTextField] = useState(false);
  const { component: SlotAddSubItemInput = AddSubItemInput, props: addSubItemInputSlotProps } =
    useSwimlaneSlot('addSubItemInput');
  const canAdd = !readOnly && subItems.length < maxSubItemsPerItem;

  const handleAddButton = () => {
    setShowTextField(!showTextField);
    listRef.current?.scrollTo({ top: 0, behavior: 'smooth' });
  };

  return (
    <div
      style={{
        maxWidth: '100%',
      }}
    >
      {expanded && (
        <div style={{ padding: 0 }}>
          <SubItemHeader count={subItems.length} onAdd={handleAddButton} canAdd={canAdd} />
          <ul
            ref={listRef}
            style={{
              maxHeight: MAX_CARD_SUBITEMS_HEIGHT.label,
              overflow: 'auto',
              padding: 0,
              margin: 0,
              listStyle: 'none',
            }}
          >
            {canAdd && (showTextField || subItems.length === 0) && (
              <div style={subItems.length > 0 ? { marginBottom: SUBITEM_GAP.label } : undefined}>
                <SlotAddSubItemInput
                  itemID={itemID}
                  rowID={rowID}
                  columnID={columnID}
                  {...addSubItemInputSlotProps}
                />
              </div>
            )}
            {subItems.map((subItem, index) => (
              <li key={`${subItem.id}`} style={{ listStyle: 'none' }}>
                <button
                  {...bindOpenCardDetailsHandler({ itemID: subItem.id, columnID, rowID })}
                  data-dd-action-name="SL sub item - redacted"
                  className="fs-exclude"
                  style={{
                    ...listItemButtonStyle,
                    marginBottom: index === subItems.length - 1 ? '0px' : SUBITEM_GAP.label,
                  }}
                >
                  <span style={listItemTextStyle}>{subItem.title}</span>
                </button>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}
